/**
 * PR-DISCOVERY-01 · P1-3 — Contextual Home CTA for Daily Check-In / Streak.
 * Surfaces existing retention mechanics (previously buried behind the 🔥 FAB)
 * as an inline, dismissable chip on Home. Tapping it opens the HomeFeedDrawer
 * (same surface where claim happens) by dispatching the existing
 * `ss2026:open-feed` event — no new mechanic, only discoverability.
 */
import { useEffect, useMemo, useState } from "react";
import { Flame, X } from "lucide-react";
import { canClaimToday } from "@/lib/retention/dailyCheckIn";
import { loadStreak } from "@/lib/retention/streaks";
import { useIsMobile } from "@/hooks/use-mobile";
import { useAuth } from "@/hooks/useAuth";
import { trackEvent } from "@/lib/audit";

const DISMISS_KEY = "vs:discovery:daily_cta:dismissed_on";

function dismissedToday(): boolean {
  if (typeof window === "undefined") return false;
  try { return window.localStorage.getItem(DISMISS_KEY) === new Date().toISOString().slice(0, 10); }
  catch { return false; }
}
function markDismissed() {
  if (typeof window === "undefined") return;
  try { window.localStorage.setItem(DISMISS_KEY, new Date().toISOString().slice(0, 10)); } catch { /* noop */ }
}

export function DiscoveryDailyCTA({ inline = false }: { inline?: boolean }) {
  const isMobile = useIsMobile();
  const { isAuthenticated } = useAuth();
  const [dismissed, setDismissed] = useState<boolean>(() => dismissedToday());
  const [state, setState] = useState<{ checkin: boolean; streak: number }>(
    () => ({ checkin: false, streak: 0 })
  );
  useEffect(() => {
    const recompute = () => {
      try {
        setState({ checkin: canClaimToday(), streak: loadStreak().consecutive_days_active });
      } catch { /* noop */ }
    };
    recompute();
    const id = window.setInterval(recompute, 60_000);
    return () => window.clearInterval(id);
  }, []);

  const message = useMemo(() => {
    if (state.checkin) return "🔥 Reclama tu recompensa diaria";
    if (state.streak >= 2) return `🔥 Mantén tu racha de ${state.streak} días`;
    return null;
  }, [state.checkin, state.streak]);

  useEffect(() => {
    if (!dismissed && isAuthenticated && message) {
      void trackEvent("discovery_home_cta_shown", {
        checkin: state.checkin, streak: state.streak,
      });
    }
  }, [dismissed, isAuthenticated, message, state.checkin, state.streak]);

  if (!isAuthenticated || dismissed || !message) return null;

  const open = () => {
    void trackEvent("discovery_home_cta_clicked", {
      reason: state.checkin ? "checkin" : "streak", streak: state.streak,
    });
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("ss2026:open-feed"));
    }
  };

  return (
    <div
      className={
        inline
          ? "inline-flex w-full items-center justify-between gap-1 rounded-2xl border border-[oklch(0.78_0.2_50)]/60 bg-background/95 py-2 pl-3 pr-1 text-[11px] font-black tracking-wider shadow-lg backdrop-blur-md"
          : "fixed left-1/2 z-20 -translate-x-1/2 inline-flex items-center gap-1 rounded-full border border-[oklch(0.78_0.2_50)]/60 bg-background/95 pl-3 pr-1 py-1 text-[11px] font-black tracking-wider shadow-lg backdrop-blur-md"
      }
      style={{
        bottom: inline ? undefined : isMobile ? "calc(env(safe-area-inset-bottom, 0px) + 178px)" : 134,
      }}
    >
      <button
        type="button"
        onClick={open}
        className="inline-flex items-center gap-1.5 text-[oklch(0.78_0.2_50)] transition hover:scale-105 active:scale-95"
        aria-label={message}
      >
        <Flame className="h-3.5 w-3.5" />
        {message}
        <span className="ml-0.5 opacity-80">→</span>
      </button>
      <button
        type="button"
        onClick={() => { setDismissed(true); markDismissed(); }}
        aria-label="Cerrar"
        className="ml-1 grid h-6 w-6 place-items-center rounded-full bg-accent/40 transition hover:bg-accent/70"
      >
        <X className="h-3 w-3" />
      </button>
    </div>
  );
}